import React from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas-tokens'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getRoomAreaWithUnits} from '@vio/core/src/room/business/utils/roomAreaUnit'
import {isUnnamedRoomName} from '@vio/core/src/room/business/utils/roomless'
import {RoomSplitType} from '@vio/core/src/room/types/RoomConfiguration'

import {RankedTopRoomAmenities} from '../RoomAmenities/RankedTopRoomAmenities'
import {RoomlessDisclaimer} from '../RoomlessDisclaimer'
import {Bedrooms} from './Bedrooms'
import {
  areExtraBedsAvailable,
  getSleepsNumber,
  isCribAvailable,
  isFreeChildStay
} from './utils'

import type {ChildPolicy} from '@findhotel/sapi'
import type {Room} from '@vio/core/src/room/types/room'

const MAX_OCCUPANCY = 8
const MIN_OCCUPANCY = 2

interface Props {
  room: Pick<
    Room,
    | 'name'
    | 'bedTypes'
    | 'amenities'
    | 'amenitiesList'
    | 'area'
    | 'bedrooms'
    | 'occupancy'
  >
  userCountry: string
  roomsSplit: RoomSplitType[]
  isMountedOnOverlay?: boolean
  displayTopAmenities?: boolean
  childPolicy?: ChildPolicy
}

const Row = styled(Box)`
  display: flex;
  align-items: center;
`

const RoomInfoWrapper = styled.div<{isMountedOnOverlay: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s300};

  ${({isMountedOnOverlay}) =>
    !isMountedOnOverlay &&
    css`
      flex-direction: row;
      flex-wrap: wrap;
      column-gap: ${cssTheme.layout.spacing.s500};
      row-gap: ${cssTheme.layout.spacing.s200};
    `}
`

export const RoomInfo = ({
  room,
  userCountry,
  roomsSplit,
  isMountedOnOverlay = false,
  displayTopAmenities = true,
  childPolicy
}: Props) => {
  const {area, amenitiesList, bedrooms} = room

  const roomArea = getRoomAreaWithUnits(area, userCountry, isMountedOnOverlay)

  const {
    allowExtraBeds = false,
    allowCribs = false,
    maxAdults = 0,
    maxOccupancy = 0
  } = room.occupancy || {}

  const isOccupancyTooHigh =
    maxAdults > MAX_OCCUPANCY || maxOccupancy > MAX_OCCUPANCY
  // We check !== 0 because maxAdults and maxOccupancy default to 0 when not provided
  // in the API response. Without this check, a room with valid maxAdults (e.g., 4) but
  // no maxOccupancy would incorrectly hide the sleeps section (since 0 < 2).
  const isOccupancyTooLow =
    (maxAdults !== 0 && maxAdults < MIN_OCCUPANCY) ||
    (maxOccupancy !== 0 && maxOccupancy < MIN_OCCUPANCY)
  const skipSleeps = isOccupancyTooHigh || isOccupancyTooLow

  const sleepsNumber = getSleepsNumber({roomsSplit, room})
  const freeChildStay = isFreeChildStay(roomsSplit, childPolicy)
  const hasOccupancy = Boolean(maxAdults) || freeChildStay

  const cribAvailable = isCribAvailable(roomsSplit, allowCribs)
  const extraBedsAvailable = areExtraBedsAvailable(roomsSplit, allowExtraBeds)
  const textVariant = isMountedOnOverlay ? 'bodyM' : 'bodyS'
  const iconSize = isMountedOnOverlay ? 'md' : 'sm'
  const iconGapSize = isMountedOnOverlay ? 's300' : 's250'

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={isMountedOnOverlay ? 's500' : 's400'}
    >
      <RoomInfoWrapper
        isMountedOnOverlay={isMountedOnOverlay}
        data-id="roomInfo.bedType"
      >
        {Boolean(bedrooms?.length) && (
          <Box
            display="flex"
            flexDirection="column"
            gap="s200"
            flexBasis="100%"
            desktopXs={{flexBasis: 'auto'}}
          >
            <Bedrooms
              bedrooms={bedrooms || []}
              size={isMountedOnOverlay ? 'md' : 'sm'}
            />
            {isMountedOnOverlay && (
              <>
                {extraBedsAvailable && (
                  <Text variant="bodyM" colorPath="content.neutral.secondary">
                    <FormattedMessageWrapper
                      id="extraBeddingInfo"
                      defaultMessage="Extra beds available on request"
                    />
                  </Text>
                )}
                {cribAvailable && (
                  <Text
                    variant={textVariant}
                    colorPath="content.neutral.secondary"
                  >
                    <FormattedMessageWrapper
                      id="cribAvailableInfo"
                      defaultMessage="Crib available on request"
                    />
                  </Text>
                )}
              </>
            )}
          </Box>
        )}
        {hasOccupancy && !skipSleeps && (
          <Box
            display="flex"
            flexDirection={isMountedOnOverlay ? 'column' : 'row'}
            alignItems={isMountedOnOverlay ? 'flex-start' : 'center'}
            flexWrap="wrap"
            gap="s200"
          >
            {Boolean(maxAdults) && (
              <Row gap={iconGapSize}>
                <Icon name="People" size={iconSize} />
                <Text variant={textVariant}>
                  <FormattedMessageWrapper
                    id="maxOccupancy"
                    defaultMessage="Sleeps {sleepsNumber}"
                    values={{
                      sleepsNumber
                    }}
                  />
                  {freeChildStay && !isMountedOnOverlay && ' · '}
                </Text>
              </Row>
            )}
            {freeChildStay && (
              <Text variant={textVariant} colorPath="content.success.secondary">
                {childPolicy?.maxAge === 0 ? (
                  <FormattedMessageWrapper
                    id="freeChildStayUnder1"
                    defaultMessage="Children under 1 stay free"
                  />
                ) : (
                  <FormattedMessageWrapper
                    id="freeChildStayRange"
                    defaultMessage="Children 0-{maxAge} stay free"
                    values={{maxAge: childPolicy?.maxAge}}
                  />
                )}
              </Text>
            )}
          </Box>
        )}
        {!isMountedOnOverlay && cribAvailable && (
          <Row gap={iconGapSize}>
            <Icon name="Crib" size={iconSize} />
            <Text variant={textVariant}>
              <FormattedMessageWrapper
                id="cribAvailableInfo"
                defaultMessage="Crib available on request"
              />
            </Text>
          </Row>
        )}
        {roomArea && (
          <Row data-id="roomInfo.area" gap={iconGapSize}>
            <Icon name="SquareFullscreen" size={iconSize} />
            <Text variant={textVariant}>
              <bdi>{roomArea}</bdi>
            </Text>
          </Row>
        )}
        {isUnnamedRoomName(room.name) && <RoomlessDisclaimer size={iconSize} />}
      </RoomInfoWrapper>

      {displayTopAmenities && (
        <RankedTopRoomAmenities
          amenitiesList={amenitiesList}
          size={isMountedOnOverlay ? 'md' : 'sm'}
        />
      )}
    </Box>
  )
}
